import { Box, useTheme } from "@mui/material";
import { CareerProps, InstituteProps, facultyProps } from "@/types";
import { tokens, useAuth } from "@/utilities";
import { useEffect, useState } from "react";
import instituteService from "@/api/institute.service";
import facultyService from "@/api/faculty.service";
import CareerInfo from "./DetailByCareer/CareerInfo";
import { ContactInfo } from "./DetailByCareer/ContactInfo";
import { FinancialSupport } from "./DetailByCareer/FinancialSupport";
import { CareerImages } from "./DetailByCareer/CareerImages";
import { HeadquarterInfo } from "./DetailByCareer/HeadquarterInfo";
import { ProgramDescription } from "./DetailByCareer/ProgramDescription";
import { GraduatedProfile } from "./DetailByCareer/GraduatedProfile";
import { CandidateProfile } from "./DetailByCareer/CandidateProfile";
import { AcademicDegree } from "./DetailByCareer/AcademicDegree";

interface Props {
  selectedCareer: CareerProps;
  updateCareer: (careerMap: Partial<CareerProps>, id: string) => void;
  institute?: InstituteProps | null;
  faculty?: facultyProps | null;
}

export const DetailByCareer = ({
  selectedCareer,
  updateCareer,
  institute: propInstitute,
  faculty: propFaculty,
}: Props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user, institute: authInstitute } = useAuth();

  const careerInstituteId =
    selectedCareer?.uidInstitution || selectedCareer?.ref?.split("/")[1] || "";
  const careerFacultyId =
    selectedCareer?.uidFaculty || selectedCareer?.ref?.split("/")[3] || "";

  const [institute, setInstitute] = useState<InstituteProps | null>(
    propInstitute ||
      (authInstitute && authInstitute.id === careerInstituteId
        ? authInstitute
        : null)
  );
  const [faculty, setFaculty] = useState<facultyProps | null>(
    propFaculty || null
  );

  useEffect(() => {
    let isMounted = true;
    async function loadParents() {
      try {
        if (!propInstitute) {
          if (authInstitute && authInstitute.id === careerInstituteId) {
            if (isMounted) setInstitute(authInstitute);
          } else if (careerInstituteId) {
            const instData = await instituteService.getById(careerInstituteId);
            if (isMounted && instData) setInstitute(instData);
          }
        } else {
          if (isMounted) setInstitute(propInstitute);
        }

        if (!propFaculty) {
          if (careerInstituteId && careerFacultyId) {
            const facData = await facultyService.getById(
              careerInstituteId,
              careerFacultyId
            );
            if (isMounted && facData) setFaculty(facData);
          }
        } else {
          if (isMounted) setFaculty(propFaculty);
        }
      } catch (error) {
        console.error("Error loading parent institute or faculty:", error);
      }
    }

    loadParents();

    return () => {
      isMounted = false;
    };
  }, [careerInstituteId, careerFacultyId, propInstitute, propFaculty, authInstitute]);

  const isEditable = (user?.subscriptionLevel ?? 0) > 1;
  const premiumPlan =
    "Para poder acceder a los campos deshabilitados, por favor, dirigete a nuestros planes y adquiere uno.";

  const fieldsToEdit = "career";
  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: "0 20px", sm: "0 50px" },
        backgroundColor: colors.primary[400],
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {selectedCareer ? (
        <>
          <Box
            sx={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              borderRadius: "5px",
              marginTop: "20px",
              padding: { xs: 0, sm: "20px", md: "50px" },
              gap: "20px",
            }}
          >
            <CareerInfo
              premiumPlan={premiumPlan}
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
              isEditable={isEditable}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              padding: { xs: 0, sm: "20px", md: "0 50px" },
              width: "100%",
            }}
          >
            <ContactInfo
              premiumPlan={premiumPlan}
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
              isEditable={isEditable}
              fieldsToEdit={fieldsToEdit}
              institute={institute}
              faculty={faculty}
            />

            <FinancialSupport
              premiumPlan={premiumPlan}
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
              isEditable={isEditable}
              fieldsToEdit={fieldsToEdit}
              institute={institute}
              faculty={faculty}
            />

            <CareerImages
              premiumPlan={premiumPlan}
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
              isEditable={isEditable}
            />

            <HeadquarterInfo
              premiumPlan={premiumPlan}
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
              isEditable={isEditable}
            />

            <ProgramDescription
              premiumPlan={premiumPlan}
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
              isEditable={isEditable}
            />

            <AcademicDegree
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
            />

            <GraduatedProfile
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
            />

            <CandidateProfile
              selectedCareer={selectedCareer}
              updateCareer={updateCareer}
            />

            <br />
            <br />
          </Box>
        </>
      ) : (
        ""
      )}
    </Box>
  );
};
